// Function index:
//   HEX_SIZE              — pixel radius of one hex.
//   VIEW_RADIUS           — client's view-ring size. Must match the server.
//   HexTile               — Honeycomb defineHex factory (pointy-top).
//   TERRAIN_COLORS        — base fill per terrain key.
//   TERRAIN_TEXTURE       — atlas frame name per terrain key (Phase 5 sprites).
//   UNDISCOVERED_DIM      — factor applied to undiscovered tiles.
//   FOGGY_DIM             — factor applied to "discovered, out of view"
//                            tiles (Phase 2.5 fog of war).
//   Visibility            — 'visible' | 'foggy' | 'unknown'.
//   SELF_TOKEN_COLOUR     — player's own token fill.
//   OTHER_TOKEN_COLOUR    — other party members' tokens.
//   NPC_TOKEN_COLOUR      — non-player tokens (smaller, neutral grey).
//   NEIGHBOURS            — six axial offsets for pointy-top adjacency.
//   baseColor(terrain, discovered) — final fill color (dimmed if undiscovered).
//   dim(color, factor)    — multiply each RGB channel.
//   drawTile(g, corners, color) / pathHex(g, corners) — Pixi drawing helpers.
//   isAdjacent(q1, r1, q2, r2) — true if hexes share an edge.

import { Graphics } from 'pixi.js';
import { defineHex, Orientation } from 'honeycomb-grid';
import type { NeighbourOffset } from './types.js';

export const HEX_SIZE = 36;
// Mirror of server/src/world/visibility.ts::VIEW_RADIUS. Phase F of the
// massive-world plan bumped it from 1 to 3 so the player can take in the
// neighbourhood at 1M-hex scale. Keep both in sync — if the server says
// 3 hexes are "in view" the client must render 37 of them as 'visible'
// rather than fog the outer 30.
export const VIEW_RADIUS = 3;
export const HexTile = defineHex({ dimensions: HEX_SIZE, orientation: Orientation.POINTY });

export const TERRAIN_COLORS: Record<string, number> = {
  grass:   0x4a7a3a,
  forest:  0x2d5a2d,
  hill:    0x7a6b3a,
  mountain:0x6a6a6a,
  water:   0x2a5a8a,
  desert:  0xb8a06a,
  unknown: 0x222222,
};

// Phase 5: atlas frame names per terrain, parallel to TERRAIN_COLORS. The
// sprite layer (hex-scene/atlas.ts + culling.ts) looks these up in
// public/atlases/terrain.json; the vector fill above stays the fallback when
// a frame (or the whole atlas) is absent.
export const TERRAIN_TEXTURE: Record<string, string> = {
  grass:    'grass',
  forest:   'forest',
  hill:     'hill',
  mountain: 'mountain',
  water:    'water',
  desert:   'desert',
};

export const UNDISCOVERED_DIM = 0.35;
export const FOGGY_DIM = 0.60;
export const SELF_TOKEN_COLOUR  = 0xffeb88;
export const OTHER_TOKEN_COLOUR = 0x88c0ff;
export const NPC_TOKEN_COLOUR   = 0xc8a878;
// Cross-party visibility: PCs from another party — muted violet so they're
// clearly neither this party (blue/gold) nor an NPC (amber).
export const FOREIGN_TOKEN_COLOUR = 0xa890d0;

export type Visibility = 'visible' | 'foggy' | 'unknown';

export const NEIGHBOURS: ReadonlyArray<NeighbourOffset> = [
  [+1, 0], [-1, 0],
  [0, +1], [0, -1],
  [+1, -1], [-1, +1],
];

// Backwards-compatible signature: existing callers pass a boolean
// (true = visible, false = unknown). New callers pass a Visibility tag for
// the three-state fog of war.
export function baseColor(terrain: string, visibility: boolean | Visibility): number {
  const c = TERRAIN_COLORS[terrain] ?? TERRAIN_COLORS.unknown!;
  const v: Visibility =
    typeof visibility === 'boolean' ? (visibility ? 'visible' : 'unknown') : visibility;
  if (v === 'visible') return c;
  if (v === 'foggy')   return dim(c, FOGGY_DIM);
  return dim(c, UNDISCOVERED_DIM);
}

export function dim(color: number, factor: number): number {
  const r = ((color >> 16) & 0xff) * factor;
  const g = ((color >> 8) & 0xff) * factor;
  const b = (color & 0xff) * factor;
  return (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b);
}

export function drawTile(g: Graphics, corners: Array<{ x: number; y: number }>, color: number): void {
  pathHex(g, corners);
  g.fill({ color }).stroke({ color: 0x000, width: 1, alpha: 0.4 });
}

export function pathHex(g: Graphics, corners: Array<{ x: number; y: number }>): void {
  const c0 = corners[0]!;
  g.moveTo(c0.x, c0.y);
  for (let i = 1; i < corners.length; i++) g.lineTo(corners[i]!.x, corners[i]!.y);
  g.closePath();
}

export function isAdjacent(q1: number, r1: number, q2: number, r2: number): boolean {
  return NEIGHBOURS.some(([dq, dr]) => q1 - q2 === dq && r1 - r2 === dr);
}
